import { SecurityConfig, EventEmitParams, EventErrorParams, EventLogParams } from '@byted/tiktok-zti-types';
import { CallEvent } from '@byted/tiktok-zti-call';
import { processRequestConfig, processResponseConfig, getHooksConfig, parseFetchHeader } from './hooks';
import { ProxyConfig } from './types';

import { parseHeaders } from './utils';

const IS_FETCH_API_SUPPORTED = 'fetch' in window;
const IS_REQUEST_API_SUPPORTED = 'Request' in window;
const IS_HEADERS_API_SUPPORTED = 'Headers' in window;

class SecureProxy extends CallEvent<{
  load: EventEmitParams;
  ready: EventEmitParams;
  execute: EventEmitParams;
  error: EventErrorParams;
  log: EventLogParams;
}> {
  params: ProxyConfig;

  config: SecurityConfig = {};

  updateData = false;

  login = false;

  // 默认pubKey
  signType?: 'cert' | 'pubKey' = 'pubKey';
  initType?: 'cert' | 'pubKey' = 'pubKey';

  private nativeXMLHttpRequestOpen = window.XMLHttpRequest.prototype.open;

  private nativeXMLHttpRequestSend = window.XMLHttpRequest.prototype.send;

  private nativeXMLHttpRequestSetRequestHeader = window.XMLHttpRequest.prototype.setRequestHeader;

  private nativeFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response> = window.fetch;

  constructor(params: ProxyConfig) {
    super();
    this.params = params;
    this.monkeyPatchXMLHttpRequest();
    this.monkeyPatchFetch();
  }

  /**
   * 设置当前加签 or 生成模式
   */
  setType = (params: { initType: 'cert' | 'pubKey'; signType: 'cert' | 'pubKey' }) => {
    const { initType = 'pubKey', signType = 'pubKey' } = params;
    this.signType = signType;
    this.initType = initType;
  };

  /**
   * 设置config
   * @param config cofig
   */
  setConfig = (config: SecurityConfig) => {
    this.config = config;
  };

  setUpdateDataWhenVerifySuccess = (update: boolean) => {
    this.updateData = update;
  };

  setLogin = (login: boolean) => {
    this.login = login;
  };

  private monkeyPatchXMLHttpRequest(): any {
    const secure = this;
    // const { crypt } = this.params || {};

    XMLHttpRequest.prototype.open = function (): void {
      // Save open arguments, we use it in `send` method.
      (this as any).secureOpenArgs = arguments;
      secure.nativeXMLHttpRequestOpen.apply(this, arguments as any);
    };
    XMLHttpRequest.prototype.send = function (): any {
      const that = this as any;
      const { secureOpenArgs } = this as any;
      const sendArgs = arguments;
      if (!that._reqHeaders) {
        that._reqHeaders = {};
      }

      const method = secureOpenArgs[0] || 'GET';
      const u = new URL(secureOpenArgs[1], window.location.href);
      const baseurl = u.toString();

      const hooksConfig = getHooksConfig(
        { method, url: baseurl, headers: {}, path: u.pathname },
        secure.config,
        secure.signType || 'pubKey',
        secure.initType || 'pubKey'
      );

      const { needProxy } = hooksConfig || {};

      if (!needProxy) {
        return secure.nativeXMLHttpRequestSend.apply(this, sendArgs as any);
      }

      // 记录当前请求头信息
      const reqHeaders: Record<string, string | number> = {};

      let reqExtras: Record<string, any> = {};

      const onreadystatechangeHandler = async (...args: any[]) => {
        if (this.readyState === 4) {
          const existOriginGetResponseHeader =
            'getAllResponseHeaders' in this && typeof this.getAllResponseHeaders === 'function';
          if (existOriginGetResponseHeader) {
            if (needProxy) {
              const headers = this.getAllResponseHeaders();
              const headerObj = parseHeaders(headers);
              await processResponseConfig(
                {
                  config: { method, url: baseurl, path: u.pathname, headers: {}, extras: reqExtras },
                  headers: headerObj || {},
                  reqHeaders
                },
                secure.params,
                hooksConfig,
                secure.updateData,
                secure.login,
                {
                  reportEvent: secure.reportEvent,
                  reportError: secure.reportError,
                  reportLog: secure.reportLog
                }
              );
            }
          }
        }
      };

      const originStateChange = this.onreadystatechange;
      const ifExistOriginCall = 'onreadystatechange' in this && typeof originStateChange === 'function';
      if (ifExistOriginCall) {
        this.onreadystatechange = async (...args) => {
          if (this.readyState === 4) {
            const existOriginGetResponseHeader =
              'getAllResponseHeaders' in this && typeof this.getAllResponseHeaders === 'function';
            if (existOriginGetResponseHeader) {
              if (needProxy) {
                const headers = this.getAllResponseHeaders();
                const headerObj = parseHeaders(headers);
                const config = await processResponseConfig(
                  {
                    config: { method, url: baseurl, headers: {}, extras: reqExtras, path: u.pathname },
                    headers: headerObj || {},
                    reqHeaders
                  },
                  secure.params,
                  hooksConfig,
                  secure.updateData,
                  secure.login,
                  {
                    reportEvent: secure.reportEvent,
                    reportError: secure.reportError,
                    reportLog: secure.reportLog
                  }
                );
              }
            }
          }
          return originStateChange.apply(this, args);
        };
      } else {
        // 如果原先没有定义，加一个
        this.onreadystatechange = onreadystatechangeHandler;
      }
      // Set secure header synchronously and send request immediately if current request is not async.
      if (secureOpenArgs.length >= 3 && !secureOpenArgs[2]) {
        return secure.nativeXMLHttpRequestSend.apply(this, sendArgs as any);
      }

      // Set request and send request asynchronously
      processRequestConfig?.(
        {
          method,
          url: baseurl,
          headers: {},
          path: u.pathname
        },
        secure.params,
        hooksConfig,
        { reportEvent: secure.reportEvent, reportError: secure.reportError, reportLog: secure.reportLog }
      ).then((config: any) => {
        const { headers = {}, extras = {} } = config || {};
        reqExtras = extras;
        Object.keys(headers).forEach((ele: string) => {
          // 缓存本次请求的请求头
          reqHeaders[ele] = headers[ele];
          if (that._reqHeaders?.[ele] === undefined) {
            // SetRequestHeader会append value, 先判断下_reqHeaders是否已有相关header
            secure.nativeXMLHttpRequestSetRequestHeader.call(this, ele, headers[ele]);
            that._reqHeaders[ele] = headers[ele];
          }
        });
        return secure.nativeXMLHttpRequestSend.apply(this, sendArgs as any);
      });
    };
  }

  private monkeyPatchFetch(): any {
    if (!IS_FETCH_API_SUPPORTED) {
      return;
    }

    const secure = this;
    // const { crypt } = this.params || {};
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.fetch = function (input: RequestInfo, init?: RequestInit): Promise<Response> {
      let url: string, method: string;
      if (IS_REQUEST_API_SUPPORTED && input instanceof Request) {
        url = input.url;
        method = input.method;
      } else {
        url = input as string;
        method = init && init.method ? init.method : 'GET';
      }

      const hooksConfig = getHooksConfig(
        { method, url, headers: {}, path: url },
        secure.config,
        secure.signType || 'pubKey',
        secure.initType || 'pubKey'
      );

      const { needProxy } = hooksConfig || {};

      if (!needProxy) {
        return secure.nativeFetch.apply(this, [input, init]);
      }

      // const u = new URL(url, window.location.href);
      return processRequestConfig({ method, url, headers: {}, path: url }, secure.params, hooksConfig, {
        reportEvent: secure.reportEvent,
        reportError: secure.reportError,
        reportLog: secure.reportLog
      }).then((config: any) => {
        const { headers, extras = {} } = config || {};

        if (IS_REQUEST_API_SUPPORTED && input instanceof Request) {
          Object.keys(headers).forEach((ele: string) => {
            input.headers.set(ele, headers[ele]);
          });
        } else {
          // eslint-disable-next-line no-param-reassign
          init = init || {};
          init.headers = init.headers || {};

          if (IS_HEADERS_API_SUPPORTED && init?.headers instanceof Headers) {
            Object.keys(headers).forEach((ele: string) => {
              (init?.headers as Headers)?.set?.(ele, headers[ele]);
            });
          } else if (init && init.headers && Array.isArray(init.headers)) {
            Object.keys(headers).forEach((ele: string) => {
              init && init.headers && Array.isArray(init.headers) && init?.headers?.push([ele, headers[ele]]);
            });
          } else {
            Object.keys(headers).forEach((ele: string) => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              (init!.headers as Record<string, string>)[ele] = headers[ele];
            });
          }
        }

        return secure.nativeFetch.apply(this, [input, init]).then((res) => {
          const resHeaders: any = {};
          if (res?.headers) {
            if (typeof res?.headers?.forEach === 'function') {
              res?.headers?.forEach?.((ele, key) => {
                resHeaders[key] = ele;
              });
            } else if (typeof res?.headers?.get === 'function') {
              const fetchHeader = parseFetchHeader(res);
              Object.keys(fetchHeader).forEach((ele) => {
                resHeaders[ele] = fetchHeader[ele];
              });
            }
            return processResponseConfig(
              {
                config: { method, url, headers: {}, extras, path: url },
                headers: resHeaders,
                reqHeaders: headers
              },
              secure.params,
              hooksConfig,
              secure.updateData,
              secure.login,
              {
                reportEvent: secure.reportEvent,
                reportError: secure.reportError,
                reportLog: secure.reportLog
              }
            )
              .then(() => res)
              .catch((error) => res);
          }
          return res;
        });
      });
    };
  }

  private reportEvent = (args: EventEmitParams) => {
    this.emit('execute', args);
  };

  private reportError = (args: EventErrorParams) => {
    this.emit('error', args);
  };

  private reportLog = (args: EventLogParams) => {
    this.emit('log', args);
  };
}

export default SecureProxy;
export { version as __version__ } from '../package.json';
